import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  useGetDietsQuery,
  useCreateDietMutation,
  useDeleteDietMutation,
  useApproveDietMutation,
  useActivateDietMutation,
  useStartAssessmentMutation,
  useCloseAssessmentMutation,
  useExtendDietMutation,
  useRejectDietMutation,
} from "../api/logbookDietApi";

import { useAuth } from "../../../context/AuthContexts";
import {
  Eye,
  CheckCircle2,
  XCircle,
  PlayCircle,
  ClipboardCheck,
  StopCircle,
  CalendarClock,
  Trash2,
  Plus,
  RefreshCw,
  Search,
  BookOpen,
  Layers,
  Clock,
  CalendarDays,
  X,
  History,
  Activity,
  ShieldAlert,
} from "lucide-react";
import { PERMS } from "../../../utils/perms";

// ✅ Security & Gate Imports
import AccessGate from "../../../components/AccessGate";

// --- Sub-components ---
function StatCard({ title, value, icon: Icon }: any) {
  return (
    <div className="bg-white border rounded-xl p-5 shadow-sm flex items-center justify-between">
      <div>
        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
          {title}
        </div>
        <div className="text-2xl font-black text-gray-900 mt-1">{value}</div>
      </div>
      <div className="p-2 bg-gray-50 rounded-lg text-gray-400">
        <Icon className="w-5 h-5" />
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const s = status?.toLowerCase();
  const themes: Record<string, string> = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-blue-50 text-blue-700 border-blue-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
    active: "bg-green-50 text-green-700 border-green-200",
    assessment_ongoing: "bg-purple-50 text-purple-700 border-purple-200",
    closed: "bg-gray-100 text-gray-600 border-gray-200",
  };

  return (
    <span
      className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase border tracking-tighter ${
        themes[s] || themes.closed
      }`}
    >
      {status}
    </span>
  );
}

export default function LogbookDietsPage() {
  const { user } = useAuth();

  // RTK hooks
  const { data = [], isLoading, refetch } = useGetDietsQuery();
  const [createDiet, { isLoading: creating }] = useCreateDietMutation();
  const [deleteDiet] = useDeleteDietMutation();
  const [approveDiet] = useApproveDietMutation();
  const [activateDiet] = useActivateDietMutation();
  const [startAssessment] = useStartAssessmentMutation();
  const [closeAssessment] = useCloseAssessmentMutation();
  const [extendDiet] = useExtendDietMutation();
  const [rejectDiet] = useRejectDietMutation();

  // Local UI State
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openCreate, setOpenCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: "",
    start_date: "",
    end_date: "",
    notes: "",
  });
  const [rejectForId, setRejectForId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [extendForId, setExtendForId] = useState<string | null>(null);
  const [extendDate, setExtendDate] = useState("");

  // --- Unified Action Handler (Confirmation + Feedback) ---
  const handleAction = async (
    actionFn: () => Promise<any>,
    successMsg: string,
    confirmMsg?: string
  ) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;

    try {
      const res: any = await actionFn();
      // The server's own message when it has one to add — activating a diet
      // carries the previous sitting's unreviewed entry applications into it,
      // and how many moved is the one thing worth reading off that click.
      alert(`Success: ${res?.message || successMsg}`);
      // Note: Ideally replace alert with a toast library like react-hot-toast
    } catch (err: any) {
      console.error("Action Error:", err);
      alert(
        `Error: ${err?.data?.message || "Operation failed. Please try again."}`
      );
    }
  };

  // Memoized Filtering Logic
  const sorted = useMemo(() => {
    return [...data].sort(
      (a: any, b: any) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [data]);

  const filtered = useMemo(() => {
    return sorted.filter((d: any) => {
      const matchQ =
        !q || `${d.title} ${d.notes}`.toLowerCase().includes(q.toLowerCase());
      const matchS =
        statusFilter === "all" ||
        d.status?.toLowerCase() === statusFilter.toLowerCase();
      return matchQ && matchS;
    });
  }, [sorted, q, statusFilter]);

  return (
    <AccessGate
      perm={PERMS.logbook_diet.access}
      fallback={
        <div className="bg-white border rounded-xl p-10 text-center">
          <ShieldAlert className="w-10 h-10 text-amber-400 mx-auto mb-4" />
          <h2 className="font-black text-gray-900 uppercase tracking-widest text-sm">
            Access Restricted
          </h2>
          <p className="text-gray-500 text-sm mt-2">
            You don't have permission to view or manage application diets.
          </p>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Header Card */}
        <div className="bg-white border rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-900">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-black text-gray-900 tracking-tight text-xl uppercase">
                  Application Diets
                </h1>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Operator: {user?.email}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => refetch()}
                className="p-2.5 rounded-xl border bg-white hover:bg-gray-50 transition-all shadow-sm"
              >
                <RefreshCw
                  className={`w-4 h-4 text-gray-600 ${
                    isLoading ? "animate-spin" : ""
                  }`}
                />
              </button>

              <AccessGate perm={PERMS.logbook_diet.create}>
                <button
                  onClick={() => setOpenCreate(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-900 text-white hover:bg-gray-800 text-sm font-bold shadow-lg shadow-gray-900/20 active:scale-95 transition-all"
                >
                  <Plus className="w-4 h-4" /> CREATE DIET
                </button>
              </AccessGate>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total Diets" value={data.length} icon={Layers} />
          <StatCard
            title="Active"
            value={data.filter((d: any) => d.status === "active").length}
            icon={Activity}
          />
          <StatCard
            title="Ongoing Assessment"
            value={
              data.filter((d: any) => d.status === "assessment_ongoing").length
            }
            icon={ClipboardCheck}
          />
          <StatCard
            title="Pending Review"
            value={data.filter((d: any) => d.status === "pending").length}
            icon={History}
          />
        </div>

        {/* Filters & Table */}
        <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 border-b bg-gray-50/50 flex flex-col md:flex-row gap-4">
            <div className="relative flex-1 group">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search title, notes..."
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-900/5 focus:border-gray-400 outline-none transition-all"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-[11px] font-black text-gray-700 outline-none focus:border-gray-400 transition-all uppercase tracking-widest"
            >
              <option value="all">ALL STATUSES</option>
              <option value="pending">PENDING</option>
              <option value="active">ACTIVE</option>
              <option value="assessment_ongoing">ONGOING</option>
              <option value="closed">CLOSED</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="bg-gray-50/50 text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                  <th className="py-4 px-6 w-16 text-center">#</th>
                  <th className="py-4 px-4">Diet Title</th>
                  <th className="py-4 px-4">Timeline</th>
                  <th className="py-4 px-4">Status</th>
                  <th className="py-4 px-4">Visibility</th>
                  <th className="py-4 px-6 text-right">Operations</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((d: any, idx) => (
                  <tr
                    key={d.id}
                    className="hover:bg-gray-50/80 transition-colors group"
                  >
                    <td className="py-4 px-6 text-center font-mono text-gray-400 text-xs">
                      {(idx + 1).toString().padStart(2, "0")}
                    </td>
                    <td className="py-4 px-4">
                      <div className="font-bold text-gray-900 tracking-tight uppercase italic text-xs">
                        {d.title}
                      </div>
                      <div className="text-[10px] text-gray-400 mt-0.5 line-clamp-1">
                        {d.notes || "No additional notes"}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1.5 text-[10px] text-gray-600 font-black">
                          <CalendarDays className="w-3 h-3" /> {d.start_date}
                        </div>
                        <div className="flex items-center gap-1.5 text-[10px] text-gray-400 font-bold">
                          <Clock className="w-3 h-3" /> {d.end_date}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <StatusBadge status={d.status} />
                    </td>
                    <td className="py-4 px-4">
                      <span
                        className={`text-[9px] font-black px-2 py-0.5 rounded-full border ${
                          d.is_active
                            ? "text-green-600 bg-green-50 border-green-100"
                            : "text-gray-300 bg-gray-50 border-gray-100"
                        }`}
                      >
                        {d.is_active ? "LIVE" : "INACTIVE"}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className="flex justify-end items-center gap-1.5">
                        {/* View Details */}
                        <AccessGate perm={PERMS.logbook_diet.detailsAccess}>
                          <Link
                            to={`/probationer-applications/admin/submissions/diets/${d.id}/categories`}
                            // /application-diets/${d.id}
                            title="View Diet Details"
                            className="p-2 border rounded-lg bg-white hover:bg-gray-900 text-gray-400 hover:text-white transition-all shadow-sm"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </Link>
                        </AccessGate>

                        {/* PENDING ACTIONS */}
                        {d.status === "pending" && (
                          <>
                            <AccessGate perm={PERMS.logbook_diet.approve}>
                              <button
                                onClick={() =>
                                  handleAction(
                                    () => approveDiet(d.id).unwrap(),
                                    "Diet successfully approved",
                                    "Confirm approval for this diet record?"
                                  )
                                }
                                title="Approve Diet Window"
                                className="p-2 border rounded-lg bg-green-50 hover:bg-green-600 text-green-700 hover:text-white transition-all"
                              >
                                <CheckCircle2 className="w-3.5 h-3.5" />
                              </button>
                            </AccessGate>
                            <AccessGate perm={PERMS.logbook_diet.reject}>
                              <button
                                onClick={() => setRejectForId(d.id)}
                                title="Reject Diet Window"
                                className="p-2 border rounded-lg bg-red-50 hover:bg-red-600 text-red-700 hover:text-white transition-all"
                              >
                                <XCircle className="w-3.5 h-3.5" />
                              </button>
                            </AccessGate>
                            <AccessGate perm={PERMS.logbook_diet.delete}>
                              <button
                                onClick={() =>
                                  handleAction(
                                    () => deleteDiet(d.id).unwrap(),
                                    "Diet permanently removed",
                                    "CRITICAL: Delete this diet record permanently?"
                                  )
                                }
                                title="Delete Diet Record"
                                className="p-2 border rounded-lg bg-white hover:bg-red-50 text-red-400 hover:text-red-600 transition-all"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </AccessGate>
                          </>
                        )}

                        {/* APPROVED ACTIONS */}
                        {d.status === "approved" && (
                          <AccessGate perm={PERMS.logbook_diet.activate}>
                            <button
                              onClick={() =>
                                handleAction(
                                  () => activateDiet(d.id).unwrap(),
                                  "Diet is now ACTIVE and visible",
                                  "Activate this diet? It will become live for users."
                                )
                              }
                              title="Activate & Go Live"
                              className="p-2 border rounded-lg bg-blue-50 hover:bg-blue-600 text-blue-700 hover:text-white transition-all"
                            >
                              <PlayCircle className="w-3.5 h-3.5" />
                            </button>
                          </AccessGate>
                        )}

                        {/* ACTIVE ACTIONS */}
                        {d.status === "active" && (
                          <AccessGate
                            perm={PERMS.logbook_diet.start_assessment}
                          >
                            <button
                              onClick={() =>
                                handleAction(
                                  () => startAssessment(d.id).unwrap(),
                                  "Candidate assessment phase started",
                                  "Are you ready to BEGIN candidate assessments for this diet?"
                                )
                              }
                              title="Begin Candidate Assessment"
                              className="p-2 border rounded-lg bg-purple-50 hover:bg-purple-600 text-purple-700 hover:text-white transition-all"
                            >
                              <ClipboardCheck className="w-3.5 h-3.5" />
                            </button>
                          </AccessGate>
                        )}

                        {/* ONGOING ACTIONS */}
                        {d.status === "assessment_ongoing" && (
                          <AccessGate
                            perm={PERMS.logbook_diet.close_assessment}
                          >
                            <button
                              onClick={() =>
                                handleAction(
                                  () => closeAssessment(d.id).unwrap(),
                                  "Assessment phase has been closed",
                                  "Are you sure you want to CLOSE assessments? This action is final."
                                )
                              }
                              title="Close Assessment Period"
                              className="p-2 border rounded-lg bg-gray-900 hover:bg-red-600 text-white transition-all"
                            >
                              <StopCircle className="w-3.5 h-3.5" />
                            </button>
                          </AccessGate>
                        )}

                        {/* SHARED ACTIONS (Extend) */}
                        {(d.status === "active" ||
                          d.status === "assessment_ongoing") && (
                          <AccessGate perm={PERMS.logbook_diet.extend}>
                            <button
                              onClick={() => setExtendForId(d.id)}
                              title="Extend Deadline"
                              className="p-2 border rounded-lg bg-amber-50 hover:bg-amber-600 text-amber-700 hover:text-white transition-all"
                            >
                              <CalendarClock className="w-3.5 h-3.5" />
                            </button>
                          </AccessGate>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* --- MODALS --- */}

        {/* 1. Create Diet Modal */}
        {openCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4">
            <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border p-8 animate-in zoom-in-95 duration-200">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-black text-gray-900 tracking-tight uppercase italic">
                    New Diet Window
                  </h2>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                    Registry Configuration
                  </p>
                </div>
                <button
                  onClick={() => setOpenCreate(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <div className="space-y-6">
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">
                    Diet Title
                  </label>
                  <input
                    className="w-full px-4 py-4 bg-gray-50 border-2 border-transparent focus:border-gray-900 rounded-xl outline-none transition-all font-black uppercase text-sm italic"
                    value={createForm.title}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, title: e.target.value })
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">
                      Start Date
                    </label>
                    <input
                      type="date"
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent focus:border-gray-900 rounded-xl outline-none text-sm font-bold"
                      value={createForm.start_date}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          start_date: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">
                      End Date
                    </label>
                    <input
                      type="date"
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent focus:border-gray-900 rounded-xl outline-none text-sm font-bold"
                      value={createForm.end_date}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          end_date: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">
                    Notes
                  </label>
                  <textarea
                    rows={3}
                    className="w-full px-4 py-4 bg-gray-50 border-2 border-transparent focus:border-gray-900 rounded-xl outline-none transition-all font-medium text-sm"
                    value={createForm.notes}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, notes: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="mt-8 flex gap-3">
                <button
                  onClick={() => setOpenCreate(false)}
                  className="flex-1 py-4 rounded-xl font-black text-[10px] uppercase tracking-widest text-gray-400 hover:text-gray-900 transition-colors"
                >
                  Cancel
                </button>
                <button
                  disabled={creating || !createForm.title}
                  onClick={() =>
                    handleAction(async () => {
                      await createDiet(createForm as any).unwrap();
                      setOpenCreate(false);
                      setCreateForm({
                        title: "",
                        start_date: "",
                        end_date: "",
                        notes: "",
                      });
                    }, "Diet initialized successfully")
                  }
                  className="flex-1 py-4 rounded-xl bg-gray-900 text-white font-black text-[10px] uppercase tracking-widest shadow-xl shadow-gray-900/20 disabled:opacity-50 transition-all"
                >
                  {creating ? "Synchronizing..." : "Initialize Diet"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 2. Reject Modal */}
        {rejectForId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border p-6">
              <h2 className="text-lg font-black text-gray-900 uppercase tracking-tight italic underline">
                Specify Rejection Reason
              </h2>
              <textarea
                className="w-full mt-4 p-4 bg-gray-50 border-2 border-transparent focus:border-red-500 rounded-xl outline-none transition-all text-sm h-32 font-bold"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setRejectForId(null)}
                  className="flex-1 py-3 text-[10px] font-black uppercase tracking-widest text-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    handleAction(async () => {
                      await rejectDiet({
                        id: rejectForId,
                        reason: rejectReason,
                      } as any).unwrap();
                      setRejectForId(null);
                      setRejectReason("");
                    }, "Diet has been rejected")
                  }
                  className="flex-1 py-3 bg-red-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-red-200"
                >
                  Confirm Rejection
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 3. Extend Modal */}
        {extendForId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border p-6">
              <h2 className="text-lg font-black text-gray-900 uppercase tracking-tight italic underline">
                Extend Deadline
              </h2>
              <input
                type="date"
                className="w-full mt-4 px-4 py-3 bg-gray-50 border-2 border-transparent focus:border-amber-500 rounded-xl outline-none font-bold text-sm"
                value={extendDate}
                onChange={(e) => setExtendDate(e.target.value)}
              />
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setExtendForId(null)}
                  className="flex-1 py-3 text-[10px] font-black uppercase tracking-widest text-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    handleAction(async () => {
                      await extendDiet({
                        id: extendForId,
                        end_date: extendDate,
                      } as any).unwrap();
                      setExtendForId(null);
                      setExtendDate("");
                    }, "Timeline updated successfully")
                  }
                  className="flex-1 py-3 bg-amber-500 text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-amber-200"
                >
                  Update Timeline
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AccessGate>
  );
}
